import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  Salesperson,
  SalespersonStats,
  CallAnalysis,
  CallFilters,
} from "../types/api";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  TrashIcon,
  FunnelIcon,
  ArrowLeftIcon,
  XMarkIcon,
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from "@heroicons/react/24/outline";
import { PlayCircleIcon } from "@heroicons/react/24/solid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import CallsPerformanceChart from "../components/charts/CallsPerformanceChart";
import CallsVolumeChart from "../components/charts/CallsVolumeChart";

const SalespersonDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [salesperson, setSalesperson] = useState<Salesperson | null>(null);
  const [stats, setStats] = useState<SalespersonStats | null>(null);
  const [calls, setCalls] = useState<CallAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [callsLoading, setCallsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { isManager } = useAuth();

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(20);

  // Audio player
  const [audioCall, setAudioCall] = useState<CallAnalysis | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Filter input state (what the user is currently editing)
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [minGrade, setMinGrade] = useState<number | "">("");
  const [maxGrade, setMaxGrade] = useState<number | "">("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [objectiveFilter, setObjectiveFilter] = useState<string>("all");
  const [redFlagsFilter, setRedFlagsFilter] = useState<string>("all");

  // Applied filter state (only updated when Apply is clicked)
  const [appliedFilters, setAppliedFilters] = useState({
    dateFrom: null as Date | null,
    dateTo: null as Date | null,
    minGrade: "" as number | "",
    maxGrade: "" as number | "",
    objectiveFilter: "all",
    redFlagsFilter: "all",
  });

  const loadSalespersonData = useCallback(async () => {
    try {
      const [salespersonResponse, statsResponse] = await Promise.all([
        apiService.getSalesperson(id!),
        apiService.getSalespersonStats(id!),
      ]);

      if (salespersonResponse.success) {
        setSalesperson(salespersonResponse.data!);
      }

      if (statsResponse.success) {
        setStats(statsResponse.data!);
      }
    } catch (error) {
      console.error("Failed to load salesperson data:", error);
      toast.error("Failed to load salesperson data");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadCalls = useCallback(async () => {
    setCallsLoading(true);
    try {
      const callFilters: CallFilters = {
        page: currentPage,
        limit,
        sortBy,
        sortOrder,
        dateFrom: appliedFilters.dateFrom?.toISOString().split("T")[0],
        dateTo: appliedFilters.dateTo?.toISOString().split("T")[0],
        minGrade: appliedFilters.minGrade === "" ? undefined : Number(appliedFilters.minGrade),
        maxGrade: appliedFilters.maxGrade === "" ? undefined : Number(appliedFilters.maxGrade),
        objectiveAchieved:
          appliedFilters.objectiveFilter === "all" ? undefined : appliedFilters.objectiveFilter,
        redFlags: appliedFilters.redFlagsFilter === "all" ? undefined : appliedFilters.redFlagsFilter,
      };

      const response = await apiService.getSalespersonCalls(id!, callFilters);

      if (response.success && response.data) {
        setCalls(response.data.calls || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || 1);
      }
    } catch (error) {
      console.error("Failed to load calls:", error);
      toast.error("Failed to load calls");
    } finally {
      setCallsLoading(false);
    }
  }, [
    id,
    currentPage,
    limit,
    sortBy,
    sortOrder,
    appliedFilters,
  ]);

  useEffect(() => {
    if (id) {
      loadSalespersonData();
    }
  }, [id, loadSalespersonData]);

  useEffect(() => {
    if (id) {
      loadCalls();
    }
  }, [id, loadCalls]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setAppliedFilters({
      dateFrom,
      dateTo,
      minGrade,
      maxGrade,
      objectiveFilter,
      redFlagsFilter,
    });
  };

  const clearFilters = () => {
    setDateFrom(null);
    setDateTo(null);
    setMinGrade("");
    setMaxGrade("");
    setObjectiveFilter("all");
    setRedFlagsFilter("all");
    setCurrentPage(1);
    setAppliedFilters({
      dateFrom: null,
      dateTo: null,
      minGrade: "",
      maxGrade: "",
      objectiveFilter: "all",
      redFlagsFilter: "all",
    });
  };

  const handleDeleteSalesperson = async () => {
    try {
      const response = await apiService.deleteSalesperson(id!);
      if (response.success) {
        toast.success("Salesperson deleted successfully");
        navigate("/employee-management");
      } else {
        toast.error(response.message || "Failed to delete salesperson");
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete salesperson",
      );
    }
    setShowDeleteConfirm(false);
  };

  const handlePlayCall = async (e: React.MouseEvent, call: CallAnalysis) => {
    e.stopPropagation();
    if (audioCall?.id === call.id) {
      if (isPlaying) {
        audioRef.current?.pause();
      } else {
        audioRef.current?.play();
      }
      return;
    }
    setAudioLoading(true);
    setAudioCall(call);
    setAudioUrl(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    try {
      const res = await apiService.getCallAudioUrl(call.id);
      if (res.success && res.data?.url) {
        setAudioUrl(res.data.url);
      } else {
        toast.error("Could not load audio for this call");
        setAudioCall(null);
      }
    } catch {
      toast.error("Could not load audio for this call");
      setAudioCall(null);
    } finally {
      setAudioLoading(false);
    }
  };

  const handleClosePlayer = () => {
    audioRef.current?.pause();
    setAudioCall(null);
    setAudioUrl(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    setVolume(v);
    if (audioRef.current) {
      audioRef.current.volume = v;
      if (v === 0) setIsMuted(true);
      else setIsMuted(false);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = Number(e.target.value);
    setCurrentTime(t);
    if (audioRef.current) audioRef.current.currentTime = t;
  };

  const formatTime = (s: number) => {
    if (!isFinite(s) || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const isInsufficientData = (call: CallAnalysis) => {
    return (
      call.analysis?.insufficient_data === true ||
      (call.average_grade === null && call.status === "completed")
    );
  };

  const formatGrade = (grade?: number | string | null, call?: CallAnalysis) => {
    if (call && isInsufficientData(call)) {
      return "N/A";
    }
    if (!grade || isNaN(Number(grade))) return "N/A";
    return parseFloat(grade.toString()).toFixed(1);
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "completed":
        return { color: "#16a34a" };
      case "processing":
      case "transcribing":
      case "analyzing":
        return { color: "#d97706" };
      case "failed":
        return { color: "#dc2626" };
      default:
        return { color: "#9ca3af" };
    }
  };

  const getGradeStyle = (grade?: number | string | null) => {
    const n = grade !== undefined && grade !== null ? Number(grade) : NaN;
    if (isNaN(n) || !n) return { color: "#9ca3af", label: "—" };
    if (n >= 8) return { color: "#16a34a", label: n.toFixed(1) };
    if (n >= 6) return { color: "#d97706", label: n.toFixed(1) };
    return { color: "#dc2626", label: n.toFixed(1) };
  };

  const getOutcome = (objectiveAchieved: boolean | null | undefined) => {
    if (objectiveAchieved == null)
      return { bg: "#f3f4f6", text: "Unknown", textColor: "#9ca3af" };
    return objectiveAchieved
      ? { bg: "#dcfce7", text: "Achieved", textColor: "#166534" }
      : { bg: "#fee2e2", text: "Not Achieved", textColor: "#dc2626" };
  };

  const hasActiveFilters =
    appliedFilters.dateFrom ||
    appliedFilters.dateTo ||
    appliedFilters.minGrade !== "" ||
    appliedFilters.maxGrade !== "" ||
    appliedFilters.objectiveFilter !== "all" ||
    appliedFilters.redFlagsFilter !== "all";

  const startRecord =
    totalCount === 0 ? 0 : Math.min((currentPage - 1) * limit + 1, totalCount);
  const endRecord =
    totalCount === 0 ? 0 : Math.min(currentPage * limit, totalCount);

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const SortIcon: React.FC<{ column: string }> = ({ column }) => {
    const active = sortBy === column;
    return sortOrder === "asc" && active ? (
      <ChevronUpIcon
        style={{ width: 12, height: 12, color: active ? "#111" : "#d1d5db" }}
      />
    ) : (
      <ChevronDownIcon
        style={{ width: 12, height: 12, color: active ? "#111" : "#d1d5db" }}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!salesperson) {
    return (
      <div className="text-center">
        <p className="text-gray-500">Salesperson not found</p>
        <Link
          to="/employee-management"
          className="text-indigo-600 hover:text-indigo-500"
        >
          Back to Employee Management
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600&family=Geist+Mono:wght@400;500&display=swap');

        .ta-root {
          font-family: 'Geist', -apple-system, sans-serif;
          color: #111;
          --border: #e5e7eb;
          --muted: #6b7280;
          --accent: #2563eb;
          --radius: 8px;
        }

        .ta-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 14px;
          background: #111;
          color: #fff;
          border: none;
          border-radius: var(--radius);
          font-size: 0.82rem;
          font-weight: 500;
          cursor: pointer;
          font-family: 'Geist', sans-serif;
          letter-spacing: -0.01em;
          transition: background 0.15s;
        }
        .ta-btn-primary:hover { background: #333; }

        .ta-btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 14px;
          background: #fff;
          color: #374151;
          border: 1px solid var(--border);
          border-radius: var(--radius);
          font-size: 0.82rem;
          font-weight: 500;
          cursor: pointer;
          font-family: 'Geist', sans-serif;
          letter-spacing: -0.01em;
          transition: all 0.15s;
        }
        .ta-btn-secondary:hover { background: #f9fafb; border-color: #d1d5db; }
        .ta-btn-secondary.active { background: #f0f0f0; border-color: #111; color: #111; }

        .ta-btn-ghost {
          display: inline-flex;
          align-items: center;
          padding: 6px 10px;
          background: none;
          color: var(--muted);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          font-size: 0.78rem;
          font-weight: 500;
          cursor: pointer;
          font-family: 'Geist', sans-serif;
          transition: all 0.15s;
        }
        .ta-btn-ghost:hover:not(:disabled) { background: #f9fafb; color: #111; }
        .ta-btn-ghost:disabled { opacity: 0.35; cursor: not-allowed; }

        .filter-panel {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 20px;
          animation: slideDown 0.15s ease;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .filter-label {
          display: block;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #9ca3af;
          margin-bottom: 6px;
        }

        .filter-input {
          width: 100%;
          padding: 7px 10px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 0.82rem;
          font-family: 'Geist', sans-serif;
          color: #111;
          background: #fff;
          outline: none;
          transition: border-color 0.15s;
          box-sizing: border-box;
        }
        .filter-input:focus { border-color: #111; }

        .filter-select {
          width: 100%;
          padding: 7px 10px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 0.82rem;
          font-family: 'Geist', sans-serif;
          color: #111;
          background: #fff;
          outline: none;
          cursor: pointer;
          transition: border-color 0.15s;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 10px center;
          padding-right: 30px;
        }
        .filter-select:focus { border-color: #111; }

        .pagination-bar {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .count-text {
          font-size: 0.8rem;
          color: var(--muted);
        }
        .count-text strong { color: #111; font-weight: 600; }

        .rows-select {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          color: var(--muted);
        }
        .rows-select select {
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 3px 8px;
          font-size: 0.78rem;
          font-family: 'Geist', sans-serif;
          color: #111;
          outline: none;
          background: #fff;
          cursor: pointer;
        }

        .page-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .page-display {
          font-size: 0.78rem;
          color: #374151;
          padding: 0 12px;
          font-variant-numeric: tabular-nums;
          font-family: 'Geist Mono', monospace;
        }

        .table-wrap {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 10px;
          overflow: hidden;
        }

        .table-wrap table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.83rem;
          table-layout: fixed;
        }

        .table-wrap td, .table-wrap th {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .table-wrap thead {
          border-bottom: 1px solid var(--border);
        }

        .table-wrap th {
          padding: 10px 16px;
          text-align: left;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: #9ca3af;
          white-space: nowrap;
          cursor: pointer;
          user-select: none;
          background: #fafafa;
        }
        .table-wrap th:hover { background: #f3f4f6; color: #374151; }

        .th-inner {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .table-wrap td {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          color: #374151;
          vertical-align: middle;
        }

        .table-wrap tbody tr:last-child td { border-bottom: none; }

        .table-wrap tbody tr {
          cursor: pointer;
          transition: background 0.1s;
        }
        .table-wrap tbody tr:hover td { background: #fafafa; }
        .table-wrap tbody tr:hover td:first-child { background: #fafafa; }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 8px;
          border-radius: 100px;
          font-size: 0.72rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .grade-value {
          font-family: 'Geist Mono', monospace;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .redflag-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 0.75rem;
          font-weight: 500;
          padding: 2px 8px;
          border-radius: 100px;
        }

        .date-cell {
          font-family: 'Geist Mono', monospace;
          font-size: 0.77rem;
          color: #9ca3af;
        }

        .file-cell {
          font-family: 'Geist Mono', monospace;
          font-size: 0.72rem;
          color: #9ca3af;
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #e5e7eb;
          border-top-color: #111;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        .empty-state {
          text-align: center;
          padding: 64px 24px;
          color: var(--muted);
          font-size: 0.85rem;
        }

        .play-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: none;
          background: transparent;
          color: #9ca3af;
          cursor: pointer;
          transition: color 0.15s, background 0.15s;
          padding: 0;
        }
        .play-btn:hover { color: #2563eb; background: #eff6ff; }
        .play-btn.active { color: #2563eb; }

        .audio-player-bar {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: #fff;
          border-top: 1px solid #e5e7eb;
          padding: 12px 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          z-index: 100;
          box-shadow: 0 -4px 24px rgba(0,0,0,0.07);
          font-family: 'Geist', sans-serif;
        }

        .audio-play-pause {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: none;
          background: #111;
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.15s;
        }
        .audio-play-pause:hover { background: #333; }

        .audio-meta {
          min-width: 160px;
          flex-shrink: 0;
        }
        .audio-meta-title {
          font-size: 0.8rem;
          font-weight: 500;
          color: #111;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 200px;
        }
        .audio-meta-date {
          font-size: 0.72rem;
          color: #9ca3af;
          font-family: 'Geist Mono', monospace;
        }

        .audio-progress-wrap {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .audio-time {
          font-size: 0.72rem;
          color: #9ca3af;
          font-family: 'Geist Mono', monospace;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .audio-slider {
          -webkit-appearance: none;
          appearance: none;
          flex: 1;
          height: 3px;
          border-radius: 2px;
          background: #e5e7eb;
          outline: none;
          cursor: pointer;
          background-image: linear-gradient(#111, #111);
          background-repeat: no-repeat;
        }
        .audio-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #111;
          cursor: pointer;
          box-shadow: 0 0 0 2px #fff, 0 0 0 3px #e5e7eb;
        }

        .audio-volume-wrap {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }
        .audio-vol-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #6b7280;
          display: inline-flex;
          align-items: center;
          padding: 0;
          transition: color 0.15s;
        }
        .audio-vol-btn:hover { color: #111; }
        .audio-vol-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 72px;
          height: 3px;
          border-radius: 2px;
          background: #e5e7eb;
          outline: none;
          cursor: pointer;
          background-image: linear-gradient(#111, #111);
          background-repeat: no-repeat;
        }
        .audio-vol-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 11px;
          height: 11px;
          border-radius: 50%;
          background: #111;
          cursor: pointer;
        }

        .audio-close-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #9ca3af;
          display: inline-flex;
          align-items: center;
          padding: 4px;
          border-radius: 4px;
          transition: color 0.15s, background 0.15s;
          flex-shrink: 0;
        }
        .audio-close-btn:hover { color: #111; background: #f3f4f6; }

        .audio-loading-spin {
          width: 18px;
          height: 18px;
          border: 2px solid #e5e7eb;
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }
      `}</style>

      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex items-center space-x-4">
          {isManager && (
            <Link
              to="/employee-management"
              className="text-gray-400 hover:text-gray-500"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </Link>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              {salesperson.firstName} {salesperson.lastName}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {salesperson.callCount || 0} calls • Average grade:{" "}
              {formatGrade(stats?.averageGrade || salesperson.avgGrade)}
            </p>
          </div>
        </div>
        {isManager && (
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Terminate
            </button>
          </div>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CallsPerformanceChart
          overallAvg={stats?.averageGrade ?? null}
          salespersonId={id}
        />
        <CallsVolumeChart salespersonId={id} />
      </div>

      {/* Calls Table */}
      <div
        className="ta-root"
        style={{ display: "flex", flexDirection: "column", gap: "16px" }}
      >
        {/* Table Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "1.1rem",
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "#111",
                margin: 0,
              }}
            >
              Call History
            </h2>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`ta-btn-secondary ${showFilters ? "active" : ""}`}
            >
              <FunnelIcon style={{ width: 13, height: 13 }} />
              Filters
              {hasActiveFilters && (
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#2563eb",
                    display: "inline-block",
                    marginLeft: 2,
                  }}
                />
              )}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="filter-panel">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: "16px",
              }}
            >
              <div>
                <label className="filter-label">Date From</label>
                <DatePicker
                  selected={dateFrom}
                  onChange={(date: Date | null) => setDateFrom(date)}
                  className="filter-input"
                  placeholderText="Start date"
                  dateFormat="MMM d, yyyy"
                />
              </div>
              <div>
                <label className="filter-label">Date To</label>
                <DatePicker
                  selected={dateTo}
                  onChange={(date: Date | null) => setDateTo(date)}
                  className="filter-input"
                  placeholderText="End date"
                  dateFormat="MMM d, yyyy"
                  minDate={dateFrom || undefined}
                />
              </div>
              <div>
                <label className="filter-label">Min Grade</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="0.1"
                  value={minGrade}
                  placeholder="1.0"
                  onChange={(e) =>
                    setMinGrade(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="filter-input"
                />
              </div>
              <div>
                <label className="filter-label">Max Grade</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="0.1"
                  value={maxGrade}
                  placeholder="10.0"
                  onChange={(e) =>
                    setMaxGrade(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="filter-input"
                />
              </div>
              <div>
                <label className="filter-label">Outcome</label>
                <select
                  value={objectiveFilter}
                  onChange={(e) => setObjectiveFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All</option>
                  <option value="true">Achieved</option>
                  <option value="false">Not Achieved</option>
                  <option value="null">Unknown</option>
                </select>
              </div>
              <div>
                <label className="filter-label">Red Flags</label>
                <select
                  value={redFlagsFilter}
                  onChange={(e) => setRedFlagsFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All</option>
                  <option value="true">Has Red Flags</option>
                  <option value="false">No Red Flags</option>
                  <option value="null">Unknown</option>
                </select>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "16px",
                paddingTop: "16px",
                borderTop: "1px solid #f3f4f6",
              }}
            >
              <button
                onClick={clearFilters}
                className="ta-btn-ghost"
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <XMarkIcon style={{ width: 12, height: 12 }} />
                Clear all
              </button>
              <button onClick={applyFilters} className="ta-btn-primary">
                Apply
              </button>
            </div>
          </div>
        )}

        {/* Pagination Bar */}
        <div className="pagination-bar">
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <span className="count-text">
              <strong>
                {startRecord}–{endRecord}
              </strong>{" "}
              of <strong>{totalCount}</strong> calls
            </span>
            <div className="rows-select">
              <span>Show</span>
              <select
                value={limit}
                onChange={(e) => handleLimitChange(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
          <div className="page-nav">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="ta-btn-ghost"
            >
              ←
            </button>
            <span className="page-display">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="ta-btn-ghost"
            >
              →
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="table-wrap">
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  {[
                    { key: "average_grade", label: "Grade", width: "80px" },
                    {
                      key: "objective_achieved",
                      label: "Outcome",
                      width: "130px",
                    },
                    { key: "red_flags", label: "Red Flags", width: "110px" },
                    { key: "created_at", label: "Date", width: "120px" },
                    { key: "audio_file_name", label: "File", width: "200px" },
                    { key: "status", label: "Status", width: "110px" },
                  ].map(({ key, label, width }) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      style={{ width }}
                    >
                      <div className="th-inner">
                        {label}
                        <SortIcon column={key} />
                      </div>
                    </th>
                  ))}
                  <th style={{ width: "52px" }} onClick={(e) => e.stopPropagation()} />
                </tr>
              </thead>
              <tbody
                style={{
                  opacity: callsLoading ? 0.4 : 1,
                  transition: "opacity 0.15s",
                }}
              >
                {calls.length === 0 && !callsLoading ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="empty-state">No calls found</div>
                    </td>
                  </tr>
                ) : (
                  calls.map((call) => {
                    const grade = isInsufficientData(call)
                      ? { color: "#d1d5db", label: "N/A" }
                      : getGradeStyle(call.average_grade ?? undefined);
                    const outcome = getOutcome(call.objective_achieved);

                    return (
                      <tr
                        key={call.id}
                        onClick={() => navigate(`/call/${call.id}`)}
                      >
                        <td>
                          <span
                            className="grade-value"
                            style={{ color: grade.color }}
                          >
                            {grade.label}
                          </span>
                        </td>
                        <td>
                          <span
                            className="status-badge"
                            style={{
                              background: outcome.bg,
                              color: outcome.textColor,
                            }}
                          >
                            {outcome.text}
                          </span>
                        </td>
                        <td
                          style={{ textAlign: "center", paddingRight: "50px" }}
                        >
                          {call.red_flags === true ? (
                            <span
                              style={{
                                display: "inline-block",
                                width: 40,
                                height: 26,
                                borderRadius: 4,
                                background: "#ef4444",
                              }}
                            />
                          ) : null}
                        </td>
                        <td>
                          <span className="date-cell">
                            {new Date(call.created_at).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </span>
                        </td>
                        <td>
                          <span className="file-cell">
                            {call.audio_file_name}
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              fontSize: "0.8rem",
                              fontWeight: 500,
                              color: getStatusStyle(call.status).color,
                              textTransform: "capitalize",
                            }}
                          >
                            {call.status}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                          {call.status === "completed" && (
                            <button
                              className={`play-btn ${audioCall?.id === call.id ? "active" : ""}`}
                              onClick={(e) => handlePlayCall(e, call)}
                              title="Play call recording"
                            >
                              {audioCall?.id === call.id && audioLoading ? (
                                <span style={{ width: 16, height: 16, border: "2px solid #e5e7eb", borderTopColor: "#2563eb", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                              ) : audioCall?.id === call.id && isPlaying ? (
                                <PauseIcon style={{ width: 16, height: 16 }} />
                              ) : (
                                <PlayCircleIcon style={{ width: 18, height: 18 }} />
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Hidden audio element */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
          onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
          onCanPlayThrough={() => { audioRef.current?.play(); }}
          style={{ display: "none" }}
        />
      )}

      {/* Audio Player Bar */}
      {audioCall && (
        <div className="audio-player-bar">
          <button
            className="audio-play-pause"
            onClick={() => {
              if (isPlaying) audioRef.current?.pause();
              else audioRef.current?.play();
            }}
            disabled={audioLoading || !audioUrl}
          >
            {audioLoading ? (
              <span className="audio-loading-spin" />
            ) : isPlaying ? (
              <PauseIcon style={{ width: 16, height: 16 }} />
            ) : (
              <PlayIcon style={{ width: 16, height: 16 }} />
            )}
          </button>

          <div className="audio-meta">
            <div className="audio-meta-title" title={audioCall.audio_file_name}>
              {audioCall.audio_file_name || "Call Recording"}
            </div>
            <div className="audio-meta-date">
              {new Date(audioCall.created_at).toLocaleDateString("en-US", {
                month: "short", day: "numeric", year: "numeric",
              })}
            </div>
          </div>

          <div className="audio-progress-wrap">
            <span className="audio-time">{formatTime(currentTime)}</span>
            <input
              type="range"
              className="audio-slider"
              min={0}
              max={duration || 0}
              step={0.1}
              value={currentTime}
              onChange={handleSeek}
              style={{
                backgroundSize: duration ? `${(currentTime / duration) * 100}% 100%` : "0% 100%",
              }}
            />
            <span className="audio-time">{formatTime(duration)}</span>
          </div>

          <div className="audio-volume-wrap">
            <button className="audio-vol-btn" onClick={toggleMute}>
              {isMuted || volume === 0 ? (
                <SpeakerXMarkIcon style={{ width: 16, height: 16 }} />
              ) : (
                <SpeakerWaveIcon style={{ width: 16, height: 16 }} />
              )}
            </button>
            <input
              type="range"
              className="audio-vol-slider"
              min={0}
              max={1}
              step={0.02}
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              style={{
                backgroundSize: `${(isMuted ? 0 : volume) * 100}% 100%`,
              }}
            />
          </div>

          <button className="audio-close-btn" onClick={handleClosePlayer} title="Close player">
            <XMarkIcon style={{ width: 16, height: 16 }} />
          </button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">
                Delete Salesperson
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete {salesperson.firstName}{" "}
                  {salesperson.lastName}? This action cannot be undone. All
                  associated call analyses linked to this employee will be
                  deleted.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={handleDeleteSalesperson}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-24 mr-2 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300"
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md w-24 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalespersonDetail;
